
<template>
  <div
    class="dialog-item"
    :class="{
      selected,
      'menu-open': isMenuOpen,
      'dark-theme': isDarkTheme
    }"
  >
    <!-- =====================================================
         MAIN CONTENT
    ====================================================== -->

    <div
      class="dialog-main"
      role="button"
      tabindex="0"
      :aria-label="`Открыть диалог с ${interlocutorName}`"
      @click="handleClick"
      @keydown.enter.prevent="handleClick"
      @keydown.space.prevent="handleClick"
    >
      <!-- AVATAR -->

      <div class="avatar-container">
        <img
          class="avatar"
          :src="avatarUrl"
          :alt="interlocutorName"
          @error="handleAvatarError"
        />
      </div>

      <!-- CONTENT -->

      <div class="info">

        <!-- NAME AND TIME -->

        <div class="header-row">
          <h3 class="name">
            {{ interlocutorName }}
          </h3>

          <span
            v-if="dialog.lastMessageDate"
            class="time"
            :title="fullMessageDate"
          >
            {{ formatTime(dialog.lastMessageDate) }}
          </span>
        </div>

        <!-- LAST MESSAGE -->

        <p
          class="last-message"
          :class="{
            'last-message-empty': !dialog.lastMessageText
          }"
        >
          {{
            dialog.lastMessageText ||
            "Сообщений пока нет"
          }}
        </p>

        <!-- TAGS -->

        <div class="tags">

          <span
            class="specific-tag"
            :title="dialog.interlocutor?.specifics || ''"
          >
            {{
              dialog.interlocutor?.specifics ||
              "Специальность не указана"
            }}
          </span>

          <span
            v-if="replyTag"
            class="reply-tag"
            :class="replyTag.className"
          >
            <span class="reply-tag-dot"></span>

            {{ replyTag.text }}
          </span>

        </div>

      </div>
    </div>

    <!-- =====================================================
         MENU
    ====================================================== -->

    <div
      ref="menuRef"
      class="dialog-menu"
      @click.stop
    >
      <button
        type="button"
        class="dots-button"
        :class="{ active: isMenuOpen }"
        title="Действия с диалогом"
        aria-label="Действия с диалогом"
        aria-haspopup="menu"
        :aria-expanded="isMenuOpen"
        @click.stop="toggleMenu"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="5" cy="12" r="1.8" fill="currentColor" />
          <circle cx="12" cy="12" r="1.8" fill="currentColor" />
          <circle cx="19" cy="12" r="1.8" fill="currentColor" />
        </svg>
      </button>

      <Transition name="dropdown">
        <div
          v-if="isMenuOpen"
          class="dropdown-menu"
          role="menu"
        >
          <!-- VIEW PROFILE -->

          <button
            type="button"
            class="dropdown-action"
            role="menuitem"
            @click.stop="openProfile"
          >
            <span class="dropdown-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="8"
                  r="3.5"
                  stroke="currentColor"
                  stroke-width="1.7"
                />
                <path
                  d="M5 20C5 16.7 8 14.5 12 14.5C16 14.5 19 16.7 19 20"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
              </svg>
            </span>

            <span>Посмотреть профиль</span>
          </button>

          <div class="dropdown-divider"></div>

          <!-- DELETE DIALOG -->

          <button
            type="button"
            class="dropdown-action danger"
            role="menuitem"
            @click.stop="deleteDialog"
          >
            <span class="dropdown-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 7H20M9 7V4H15V7M6.5 7L7.5 20H16.5L17.5 7"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M10 11V16M14 11V16"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
              </svg>
            </span>

            <span>Удалить диалог</span>
          </button>

        </div>
      </Transition>
    </div>

  </div>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
  onMounted,
  onBeforeUnmount
} from "vue";

import { useRouter } from "vue-router";
import emptyAvatar from "../../assets/default-avatar.png";

// ============================================================
// ROUTER
// ============================================================

const router = useRouter();

// ============================================================
// PROPS
// ============================================================

const props = defineProps({
  dialog: {
    type: Object,
    required: true
  },

  selected: {
    type: Boolean,
    default: false
  }
});

// ============================================================
// EMITS
// ============================================================

const emit = defineEmits([
  "select",
  "openProfile",
  "delete"
]);

// ============================================================
// STATE
// ============================================================

const isMenuOpen = ref(false);
const menuRef = ref(null);
const avatarHasError = ref(false);

// ============================================================
// INTERLOCUTOR
// ============================================================

const interlocutorName = computed(() => {
  return (
    props.dialog.interlocutor?.name?.trim() ||
    "Без имени"
  );
});

// ============================================================
// AVATAR
// ============================================================

const avatarUrl = computed(() => {
  const avatar = props.dialog.interlocutor?.avatar;

  if (
    avatarHasError.value ||
    typeof avatar !== "string" ||
    !avatar.trim()
  ) {
    return emptyAvatar;
  }

  return avatar;
});

function handleAvatarError() {
  avatarHasError.value = true;
}

watch(
  () => props.dialog.interlocutor?.avatar,
  () => {
    avatarHasError.value = false;
  }
);

// ============================================================
// SELECT DIALOG
// ============================================================

function handleClick() {
  isMenuOpen.value = false;

  emit("select", props.dialog.id);
}

// ============================================================
// MENU
// ============================================================

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value;
}

function closeMenu() {
  isMenuOpen.value = false;
}

function handleOutsideClick(event) {
  if (!isMenuOpen.value) return;

  if (
    menuRef.value &&
    !menuRef.value.contains(event.target)
  ) {
    closeMenu();
  }
}

function handleGlobalKeydown(event) {
  if (event.key === "Escape") {
    closeMenu();
  }
}

watch(
  () => props.selected,
  () => {
    closeMenu();
  }
);

// ============================================================
// OPEN PROFILE
// ============================================================

function openProfile() {
  closeMenu();

  const userId = props.dialog.interlocutor?.userId;

  if (!userId) {
    console.warn(
      "Не удалось открыть профиль: отсутствует userId"
    );
    return;
  }

  router.push(`/user/${encodeURIComponent(userId)}`);
}

// ============================================================
// DELETE DIALOG
// ============================================================

function deleteDialog() {
  closeMenu();

  emit("delete", props.dialog);
}

// ============================================================
// TIME
// ============================================================

function formatTime(dateString) {
  if (!dateString) return "";

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const now = new Date();

  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  const yesterday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 1
  );

  // Сегодня — показываем часы и минуты
  if (date >= today) {
    return date.toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  }

  // Вчера
  if (date >= yesterday) {
    return "Вчера";
  }

  // Текущий год
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short"
    });
  }

  // Предыдущие годы
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
}

const fullMessageDate = computed(() => {
  if (!props.dialog.lastMessageDate) return "";

  const date = new Date(props.dialog.lastMessageDate);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString("ru-RU");
});

// ============================================================
// REPLY TAG
// ============================================================

const replyTag = computed(() => {
  switch (props.dialog.chatType) {
    case "connect":
      return {
        text: "Коннект",
        className: "connect"
      };

    case "default":
    case "response":
      return {
        text: "Отклик",
        className: "response"
      };

    default:
      return null;
  }
});

// ============================================================
// LIFECYCLE
// ============================================================

// ============================================================
// LOCAL THEME
// ============================================================

const isDarkTheme = ref(false);
let themeObserver = null;

onMounted(() => {
  const updateTheme = () => {
    isDarkTheme.value =
      document.documentElement.getAttribute("data-theme") === "dark";
  };

  updateTheme();

  themeObserver = new MutationObserver(updateTheme);

  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"]
  });
});

onBeforeUnmount(() => {
  themeObserver?.disconnect();
  themeObserver = null;
});
onBeforeUnmount(() => {
  document.removeEventListener(
    "pointerdown",
    handleOutsideClick
  );

  document.removeEventListener(
    "keydown",
    handleGlobalKeydown
  );
});
</script>


<style scoped>

/* ============================================================
   DIALOG ITEM — LIGHT THEME
============================================================ */

.dialog-item {
  --dialog-bg: #ffffff;
  --dialog-text: #222b38;
  --dialog-muted: #959daa;
  --dialog-secondary: #707a88;
  --dialog-border: rgba(28, 45, 70, 0.07);
  --dialog-hover: #f7faff;
  --dialog-tag-bg: #f5f7fa;
  --dialog-menu-bg: #ffffff;

  --dialog-accent: #2173e5;
  --dialog-active-bg: rgba(33, 115, 229, 0.075);
  --dialog-active-border: rgba(33, 115, 229, 0.28);

  position: relative;

  width: 380px;
  max-width: 100%;

  margin-bottom: 6px;

  border-radius: 18px;
  border: 1px solid var(--dialog-border);

  background: var(--dialog-bg);

  box-shadow:
    0 2px 10px rgba(20, 38, 64, 0.025);

  box-sizing: border-box;

  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

/* ============================================================
   DARK THEME — LOCAL CLASS
============================================================ */

.dialog-item.dark-theme {
  --dialog-bg: #161616;
  --dialog-text: #f1f3f6;
  --dialog-muted: #858b95;
  --dialog-secondary: #a3aab5;
  --dialog-border: rgba(255, 255, 255, 0.08);
  --dialog-hover: #202020;
  --dialog-tag-bg: #242424;
  --dialog-menu-bg: #202020;

  --dialog-accent: #83b6ff;
  --dialog-active-bg: rgba(33, 115, 229, 0.14);
  --dialog-active-border: rgba(111, 168, 255, 0.27);

  box-shadow:
    0 2px 12px rgba(0, 0, 0, 0.12);
}

/* ============================================================
   HOVER
============================================================ */

.dialog-item:hover {
  background: var(--dialog-hover);

  border-color: rgba(33, 115, 229, 0.13);

  box-shadow:
    0 6px 20px rgba(31, 75, 139, 0.06);
}

.dialog-item.dark-theme:hover {
  border-color: rgba(111, 168, 255, 0.18);

  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.18);
}

/* ============================================================
   SELECTED
============================================================ */

.dialog-item.selected {
  background:
    linear-gradient(
      115deg,
      rgba(33, 115, 229, 0.075),
      rgba(33, 115, 229, 0.025)
    );

  border-color: var(--dialog-active-border);

  box-shadow:
    0 5px 18px rgba(33, 115, 229, 0.075);
}

.dialog-item.dark-theme.selected {
  background:
    linear-gradient(
      115deg,
      rgba(33, 115, 229, 0.16),
      rgba(33, 115, 229, 0.055)
    );

  border-color: var(--dialog-active-border);

  box-shadow:
    0 5px 18px rgba(0, 0, 0, 0.16);
}

.dialog-item.selected::before {
  content: "";

  position: absolute;

  left: -1px;
  top: 18px;
  bottom: 18px;

  width: 3px;

  background: var(--dialog-accent);

  border-radius: 0 4px 4px 0;
}

.dialog-item.menu-open {
  z-index: 60;
}

/* ============================================================
   MAIN
============================================================ */

.dialog-main {
  width: 100%;

  display: flex;
  align-items: center;

  gap: 13px;

  padding: 15px 42px 15px 13px;

  box-sizing: border-box;

  cursor: pointer;
  border-radius: inherit;

  outline: none;
}

.dialog-main:focus-visible {
  outline: 2px solid var(--dialog-accent);
  outline-offset: 2px;
}

/* ============================================================
   AVATAR CONTAINER
============================================================ */

.avatar-container {
  position: relative;

  width: 54px;
  height: 54px;

  flex-shrink: 0;

  padding: 3px;

  border-radius: 17px;

  background:
    linear-gradient(
      145deg,
      rgba(33, 115, 229, 0.16),
      rgba(123, 97, 255, 0.07)
    );

  box-sizing: border-box;
}

.dialog-item.dark-theme .avatar-container {
  background:
    linear-gradient(
      145deg,
      rgba(111, 168, 255, 0.2),
      rgba(123, 97, 255, 0.08)
    );
}

/* ============================================================
   AVATAR
============================================================ */

.avatar {
  width: 100%;
  height: 100%;

  display: block;

  border-radius: 14px;

  object-fit: cover;

  background: #eef2f7;

  transition: transform 0.2s ease;
}

.dialog-item.dark-theme .avatar {
  background: #292929;
}

.dialog-item:hover .avatar {
  transform: scale(1.025);
}

/* ============================================================
   INFO
============================================================ */

.info {
  min-width: 0;
  flex: 1;

  display: flex;
  flex-direction: column;

  gap: 7px;
}

/* ============================================================
   HEADER ROW
============================================================ */

.header-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  gap: 8px;

  min-width: 0;
}

/* ============================================================
   NAME
============================================================ */

.name {
  min-width: 0;
  flex: 1;

  margin: 0;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;

  color: var(--dialog-text);

  letter-spacing: -0.15px;
}

.dialog-item.selected .name {
  color: var(--dialog-accent);
}

/* ============================================================
   TIME
============================================================ */

.time {
  flex-shrink: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1.3;

  color: var(--dialog-muted);

  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* ============================================================
   LAST MESSAGE
============================================================ */

.last-message {
  width: 100%;
  min-width: 0;

  margin: 0;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11.5px;
  line-height: 1.4;

  color: var(--dialog-secondary);
}

.last-message-empty {
  color: var(--dialog-muted);

  font-style: italic;
}

/* ============================================================
   TAGS
============================================================ */

.tags {
  display: flex;
  align-items: center;

  gap: 6px;

  width: 100%;
  min-width: 0;

  overflow: hidden;
}

/* ============================================================
   SPECIFIC TAG
============================================================ */

.specific-tag {
  display: inline-flex;
  align-items: center;

  min-width: 0;
  max-width: 155px;

  padding: 5px 9px;

  border-radius: 7px;

  border: 1px solid rgba(45, 65, 95, 0.055);

  background: var(--dialog-tag-bg);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  line-height: 1;

  color: var(--dialog-secondary);

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  box-sizing: border-box;
}

.dialog-item.dark-theme .specific-tag {
  border-color: rgba(255, 255, 255, 0.045);
}

/* ============================================================
   REPLY TAG
============================================================ */

.reply-tag {
  display: inline-flex;
  align-items: center;

  gap: 5px;

  flex-shrink: 0;

  padding: 5px 8px;

  border: 1px solid transparent;
  border-radius: 7px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
  line-height: 1;
  font-weight: 500;

  white-space: nowrap;
}

.reply-tag-dot {
  width: 5px;
  height: 5px;

  flex-shrink: 0;

  border-radius: 50%;

  background: currentColor;
}

/* ============================================================
   CONNECT TAG
============================================================ */

.reply-tag.connect {
  color: #2878e7;

  background: rgba(33, 115, 229, 0.075);

  border-color: rgba(33, 115, 229, 0.075);
}

.dialog-item.dark-theme .reply-tag.connect {
  color: #83b6ff;

  background: rgba(65, 143, 255, 0.12);

  border-color: rgba(111, 168, 255, 0.12);
}

/* ============================================================
   RESPONSE TAG
============================================================ */

.reply-tag.response {
  color: #218c59;

  background: rgba(39, 183, 130, 0.08);

  border-color: rgba(39, 183, 130, 0.075);
}

.dialog-item.dark-theme .reply-tag.response {
  color: #6bd3a4;

  background: rgba(39, 183, 130, 0.11);

  border-color: rgba(39, 183, 130, 0.12);
}

/* ============================================================
   MENU POSITION
============================================================ */

.dialog-menu {
  position: absolute;

  top: 9px;
  right: 9px;

  z-index: 20;
}

/* ============================================================
   MENU BUTTON
============================================================ */

.dots-button {
  width: 29px;
  height: 29px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: none;
  border-radius: 9px;

  color: #9ca6b3;

  background: transparent;

  cursor: pointer;

  transition:
    background 0.18s ease,
    color 0.18s ease;
}

.dots-button svg {
  width: 19px;
  height: 19px;
}

.dots-button:hover,
.dots-button.active {
  color: #2173e5;

  background: rgba(33, 115, 229, 0.09);
}

.dialog-item.dark-theme .dots-button {
  color: #8b96a5;
}

.dialog-item.dark-theme .dots-button:hover,
.dialog-item.dark-theme .dots-button.active {
  color: #83b6ff;

  background: rgba(111, 168, 255, 0.12);
}

.dots-button:focus-visible {
  outline: 2px solid var(--dialog-accent);
  outline-offset: 2px;
}

/* ============================================================
   DROPDOWN MENU
============================================================ */

.dropdown-menu {
  position: absolute;

  top: 35px;
  right: 0;

  width: 205px;

  padding: 7px;

  border-radius: 14px;

  background: var(--dialog-menu-bg);

  border: 1px solid var(--dialog-border);

  box-shadow:
    0 16px 40px rgba(20, 33, 55, 0.16),
    0 3px 9px rgba(20, 33, 55, 0.045);

  z-index: 100;
}

.dialog-item.dark-theme .dropdown-menu {
  box-shadow:
    0 18px 45px rgba(0, 0, 0, 0.38);
}

/* ============================================================
   DROPDOWN ACTIONS
============================================================ */

.dropdown-action {
  width: 100%;
  min-height: 39px;

  display: flex;
  align-items: center;

  gap: 10px;

  padding: 9px 10px;

  border: none;
  border-radius: 9px;

  background: transparent;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;

  color: var(--dialog-text);

  text-align: left;
  cursor: pointer;

  transition:
    background 0.16s ease,
    color 0.16s ease;
}

.dropdown-action:hover {
  background: rgba(33, 115, 229, 0.075);

  color: #2173e5;
}

.dialog-item.dark-theme .dropdown-action:hover {
  background: rgba(111, 168, 255, 0.1);

  color: #83b6ff;
}

.dropdown-action:focus-visible {
  outline: 2px solid var(--dialog-accent);
  outline-offset: -2px;
}

/* ============================================================
   DROPDOWN ICON
============================================================ */

.dropdown-icon {
  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: currentColor;
}

.dropdown-icon svg {
  width: 19px;
  height: 19px;
}

/* ============================================================
   DROPDOWN DIVIDER
============================================================ */

.dropdown-divider {
  height: 1px;

  margin: 5px 8px;

  background: var(--dialog-border);
}

/* ============================================================
   DELETE ACTION
============================================================ */

.dropdown-action.danger {
  color: #dc5050;
}

.dropdown-action.danger:hover {
  color: #e23c3c;

  background: rgba(226, 60, 60, 0.075);
}

.dialog-item.dark-theme .dropdown-action.danger {
  color: #ff7777;
}

.dialog-item.dark-theme .dropdown-action.danger:hover {
  color: #ff9090;

  background: rgba(235, 87, 87, 0.12);
}

/* ============================================================
   DROPDOWN ANIMATION
============================================================ */

.dropdown-enter-active,
.dropdown-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;

  transform:
    translateY(-6px)
    scale(0.97);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;

  transform: translateY(0) scale(1);
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 430px) {

  .dialog-item {
    width: 100%;
    max-width: 100%;

    border-radius: 15px;
  }

  .dialog-main {
    padding: 12px 40px 12px 11px;

    gap: 11px;
  }

  .avatar-container {
    width: 49px;
    height: 49px;

    border-radius: 15px;
  }

  .avatar {
    border-radius: 12px;
  }

  .name {
    font-size: 12.5px;
  }

  .last-message {
    font-size: 11px;
  }

  .specific-tag {
    max-width: 135px;
  }

  .time {
    font-size: 9px;
  }
}

/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .dialog-item,
  .avatar,
  .dots-button,
  .dropdown-action,
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: none;
  }

}

</style>
